/**
 * Business break announcement.
 *
 * A dialog the visitor has to acknowledge. The close button stays disabled, and
 * Escape and backdrop clicks are refused, until the confirmation box is ticked:
 * trade customers plan orders around the last order date, so this is the one notice
 * that should not be dismissable by reflex.
 *
 * There is always a way out (tick the box, press the button), so this is a gate
 * rather than a trap.
 */
class BusinessBreak extends HTMLElement {
  #dialog = null;
  #confirm = null;
  #close = null;
  #reopening = false;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    this.#confirm = this.querySelector('[data-break-confirm]');
    this.#close = this.querySelector('[data-break-close]');
    if (!this.#dialog || !this.#confirm || !this.#close) return;

    // Liquid already refuses to render an expired break, but pages are cached, so a
    // stale copy can outlive the end date. Re-check against the visitor's own clock.
    const expires = Number(this.dataset.expires || 0);
    if (expires > 0 && Date.now() / 1000 >= expires) {
      this.remove();
      return;
    }

    if (this.#alreadyAcknowledged()) {
      this.remove();
      return;
    }

    this.#confirm.addEventListener('change', () => {
      this.#close.disabled = !this.#confirm.checked;
    });

    this.#close.addEventListener('click', () => this.#dismiss());

    // Escape fires `cancel`, and refusing that is the clean path: no flicker.
    this.#dialog.addEventListener('cancel', (event) => {
      if (!this.#confirm.checked) event.preventDefault();
    });

    // But the theme's own dialog.js answers Escape by calling close() directly, which
    // fires no `cancel` at all, so preventing it there is not enough. Swallow Escape
    // before it reaches that handler.
    this.#dialog.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && !this.#confirm.checked) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      true
    );

    // Last line of defence: whatever closed it, if the box is not ticked it reopens.
    // This covers any other code calling close() on it, now or later.
    this.#dialog.addEventListener('close', () => {
      if (this.#confirm.checked || this.#reopening) return;
      this.#reopening = true;
      try {
        this.#dialog.showModal();
      } finally {
        this.#reopening = false;
      }
    });

    // A click on the backdrop lands on the dialog element itself rather than its
    // contents. Ignore it; the button is the only way out.
    this.#dialog.addEventListener('click', (event) => {
      if (event.target === this.#dialog) event.stopPropagation();
    });

    this.#dialog.showModal();
  }

  /**
   * The key is a digest of the dates and wording, so the next shutdown's notice is a
   * different key and shows again to everyone who dismissed the last one.
   */
  #alreadyAcknowledged() {
    // Always show in the theme editor, otherwise it can only be previewed once.
    if (window.Shopify?.designMode) return false;
    try {
      return localStorage.getItem(this.dataset.ackKey) === '1';
    } catch {
      // Private browsing and blocked site data both throw. Showing the notice again
      // is the right failure: it is a message they are meant to see.
      return false;
    }
  }

  #dismiss() {
    if (!this.#confirm.checked) return;
    try {
      localStorage.setItem(this.dataset.ackKey, '1');
    } catch {
      // Not storable, so it shows again next visit. Acceptable for a one week notice.
    }
    this.#dialog.close();
  }
}

if (!customElements.get('business-break')) {
  customElements.define('business-break', BusinessBreak);
}
